import { datadogRum } from '@datadog/browser-rum';
import { publicarPageviewVirtual } from '@shared/marketing-tracking/utils/dataLayerMarketing';
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import * as Boletos from '@/blocks/boletos';
import * as DeeplinkCaptacao from '@/blocks/deeplink-login';
import * as Home from '@/blocks/home';
import * as Propostas from '@/blocks/propostas';
import { Breadcrumb } from '@/composables/useBreadcrumbs';
import { checkoutFeature } from '@/features/checkout';
import { redirectLegacyOfertaToCheckout } from '@/features/checkout/routing/legacyOfertasRedirect';
import { creditoFeature } from '@/features/credito';
import {
  buildLogoutRedirect,
  persistRequestedRouteQuery,
  shouldFollowPropagatedRedirect,
} from '@/helpers/deepLinkRouterUtils';
import { normalizePropagatedQuery, popQueryOrParam } from '@/helpers/routerUtils';
import guardRouteRules from '@/router/guardRouteRules';
import { setRouter } from '@/router/indexRouter';
import { registerRouterScrollToTop } from '@/router/scrollToTop';
import { sessionService, storageService, themeService } from '@/services';
import adoptlyService from '@/services/AdoptlyService';
import serverTimeService from '@/services/ServerTimeService';
import useAreaDeslogadaStore from '@/stores/areaDeslogada';
import useDeeplink from '@/stores/deeplink';
import useGlobalStore from '@/stores/global';

declare module 'vue-router' {
  interface RouteMeta {
    allowAnonymous?: boolean;
    disableNavigation?: true;
    breadcrumbs?: Breadcrumb;
    appBg?: string;
    /** Se `true`, rola a página ao topo ao navegar para esta rota. */
    scrollToTop?: boolean;
    /** Se `true`, exibe o rodapé padrão nesta rota. Por padrão o rodapé fica oculto. */
    showFooter?: boolean;
  }
}

const routes: Array<RouteRecordRaw> = [
  ...creditoFeature.routes,
  ...checkoutFeature.routes,
  {
    path: '/',
    component: Home.component,
    children: Home.children,
    beforeEnter: to => {
      if (to.path !== '/dividas' && to.name !== 'atalho-2-via-boleto') {
        return { ...to, path: '/dividas', replace: true };
      }
      return true;
    },
  },
  {
    path: '/login',
    component: () => import('@/views/login.vue'),
    meta: {
      allowAnonymous: true,
      breadcrumbs: {
        name: 'Login',
        parent: {
          name: 'Home',
          url: appSettings.VUE_APP_BREADCRUMBS_HOME,
        },
      },
    },
  },
  {
    path: '/logout',
    meta: {
      allowAnonymous: true,
    },
    component: () => import('@/views/logout.vue'),
  },
  {
    path: '/cadastro',
    component: () => import('@/views/cadastro.vue'),
    meta: { allowAnonymous: true },
    beforeEnter: [
      () => {
        const areaDeslogadaStore = useAreaDeslogadaStore();
        if (!areaDeslogadaStore.cadastroForm.data.login) return '/login';
        return true;
      },
    ],
  },
  {
    path: '/confirmacao-email',
    component: () => import('@/views/confirmacao-email.vue'),
    meta: { allowAnonymous: true },
  },
  {
    path: '/simple-sign-on',
    component: () => import('@/views/deeplink-view.vue'),
    meta: { allowAnonymous: true, disableNavigation: true },
  },
  {
    path: '/simple-sign-on/:targetRoute',
    component: () => import('@/views/deeplink-view.vue'),
    meta: { allowAnonymous: true, disableNavigation: true },
  },
  {
    path: '/divida/:api/:id/erro-proposta',
    name: 'erro-proposta',
    component: () => import('@/blocks/home/views/ErroProposta.vue'),
    props: route => {
      const api = popQueryOrParam(route.params.api);
      const id = popQueryOrParam(route.params.id);

      return {
        api,
        id: id ? Number.parseInt(id, 10) : Number.NaN,
      };
    },
  },
  {
    name: 'erro-oferta',
    path: '/divida/:api/:dividaId/erro-oferta',
    component: () => import('@/blocks/home/views/ErroOferta.vue'),
  },
  {
    name: 'erro-divida',
    path: '/divida/:api/:dividaId/erro-divida',
    component: () => import('@/blocks/home/views/ErroDivida.vue'),
    props: route => ({
      api: route.params.api as string,
      dividaId: Number.parseInt(route.params.dividaId as string, 10),
    }),
  },
  {
    path: '/divida/:credor/autenticacao',
    name: 'autenticacao-2fa',
    component: () => import('@/views/autenticacao.vue'),
  },
  {
    path: '/divida/:api/:propostaId/detalhes',
    name: 'proposta-detalhes',
    component: () => import('@/blocks/home/views/ViewAcordoDetalhes.vue'),
    props: route => {
      const api = popQueryOrParam(route.params.api);
      const propostaId = popQueryOrParam(route.params.propostaId);
      const isAcordoExterno = route.query['acordo-externo'] === 'true';

      return {
        api,
        propostaId: propostaId ? Number.parseInt(propostaId, 10) : Number.NaN,
        isAcordoExterno,
      };
    },
  },
  {
    path: '/divida/:api/:idProposta(\\d+)/cancelar-acordo',
    name: 'cancelar-acordo',
    component: () => import('@/views/cancelar-acordo.vue'),
  },
  // LEGADO: fluxo de ofertas substituído pelo checkout. As rotas seguem registradas só como
  // redirecionamento porque links antigos (e-mail, SMS, push, histórico do navegador) ainda
  // apontam para elas.
  {
    name: 'ofertas',
    path: '/divida/:api/:dividaId/ofertas/:filtro(parciais|integrais)?',
    redirect: redirectLegacyOfertaToCheckout,
  },
  {
    name: 'opcoes-para-negociar',
    path: '/divida/:api/:dividaId/ofertas/tipo-negociacao',
    redirect: redirectLegacyOfertaToCheckout,
  },
  {
    name: 'detalhes-oferta',
    path: '/divida/:api/:dividaId/oferta/:ofertaId',
    redirect: redirectLegacyOfertaToCheckout,
  },
  ...Boletos.routes,
  ...Propostas.routes,
  ...DeeplinkCaptacao.routes,
  {
    path: '/:catchAll(.*)',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(appSettings.BASE_URL),
  routes,
});

setRouter(router);
registerRouterScrollToTop(router);

router.beforeEach(async (to, from) => {
  const globalStore = useGlobalStore();
  globalStore.routeLoading = true;

  await serverTimeService.waitForSync({ timeout: 3000, delay: 100 }).then(
    () => {
      const { timeDiff } = serverTimeService;
      datadogRum.setGlobalContextProperty('serverTimeDiff', timeDiff);
      storageService.serverTime.set({ savedAt: Date.now(), timeDiff });
    },
    () => {
      // ignorar erros e prosseguir
    }
  );

  sessionService.reload();

  if (!to.meta.allowAnonymous && !sessionService.isLoggedIn) {
    persistRequestedRouteQuery(to.query);
    return buildLogoutRedirect(to.path);
  }

  const guardOutcome = guardRouteRules(to, from);
  if (guardOutcome !== true) return guardOutcome;

  const queryObject = normalizePropagatedQuery(to.query);

  const redirect = popQueryOrParam(queryObject.redirect);
  const deeplinkStore = useDeeplink();
  if (
    shouldFollowPropagatedRedirect({
      isLoggedIn: sessionService.isLoggedIn,
      isDeepLinkJourneyActive: deeplinkStore.isDeepLinkJourneyActive,
      redirect,
      toPath: to.path,
    })
  ) {
    return redirect;
  }

  return true;
});

router.afterEach((to, from, failure) => {
  const globalStore = useGlobalStore();
  globalStore.routeLoading = false;
  if (failure) {
    return;
  }

  useDeeplink();

  themeService.overrideAppBg(to.meta.appBg);
  datadogRum.startView(String(to.name || to.path));
  adoptlyService.pageChanged();
  publicarPageviewVirtual(to);

  const queryObject = normalizePropagatedQuery(to.query);

  if (queryObject.redirect === to.path) {
    delete queryObject.redirect;
  }

  const removeAcordoExternoOnLeave = [
    'proposta-detalhes',
    'boletos',
    'proposta-aceita',
    'pagamento-parcela-boleto',
    'pagamento-parcela-pix',
  ];

  if (
    removeAcordoExternoOnLeave.includes(from.name as string) &&
    !removeAcordoExternoOnLeave.includes(to.name as string)
  ) {
    delete queryObject['acordo-externo'];
  }

  const urlParamsString = new URLSearchParams(queryObject).toString();
  storageService.queryString.set(urlParamsString);
  if (urlParamsString) {
    window.history.replaceState(window.history.state, '', `?${urlParamsString}`);
  }
});

router.onError(() => {
  const globalStore = useGlobalStore();
  globalStore.routeLoading = false;
});

router.beforeResolve(() => {
  const globalStore = useGlobalStore();
  globalStore.routeLoading = false;
});

export default router;
